import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { NgxSpinnerService } from 'ngx-spinner';
import { LanguageTranslateService } from 'src/app/_services/language-translate.service';
import { ShiftsService } from 'src/app/_services/shifts.service';
import { ErrorUtil } from 'src/app/_utilities/error';
import { SubscriptionUtil } from 'src/app/_utilities/subscription';
import * as FileSaver from 'file-saver';

interface ValidationRow {
  rowNumber: number;
  identifier: string;
  shiftCode: string;
  start: string;
  end: string;
  isValid: boolean;
  errorReason?: string;
}

interface CsvSourceRow {
  rowNumber: number;
  assigneeType: string;
  identifier: string;
  shiftCode: string;
  start: string;
  end: string;
}

@Component({
  selector: 'app-import-bulk-shifts',
  standalone: false,
  templateUrl: './import-bulk-shifts.component.html',
  styleUrl: './import-bulk-shifts.component.scss',
})
export class ImportBulkShiftsComponent extends SubscriptionUtil implements OnInit, OnDestroy {
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  translate: any;
  data: any;
  uploadedFileName: string | null = null;
  selectedFile: File | null = null;
  validationRows: ValidationRow[] = [];
  private csvSourceRows: CsvSourceRow[] = [];
  downloadingTemplate = false;
  validating = false;
  applying = false;

  constructor(
    public modalRef: BsModalRef,
    private languageTranslateService: LanguageTranslateService,
    private shiftsService: ShiftsService,
    private spinner: NgxSpinnerService,
    private errorUtil: ErrorUtil,
  ) {
    super();
  }

  ngOnInit(): void {
    this.getLanguageData();
  }

  getLanguageData() {
    this.push(
      this.languageTranslateService.wordSrc$.subscribe((data) => {
        if (Object.keys(data).length != 0) {
          this.translate = data;
        }
      })
    );
  }

  get validCount(): number {
    return this.validationRows.filter((r) => r.isValid).length;
  }

  get errorCount(): number {
    return this.validationRows.filter((r) => !r.isValid).length;
  }

  /** Step 4 status based on validation results */
  get confirmApplyStatus(): 'success' | 'partial' | 'failed' {
    if (!this.validationRows.length || this.validCount === 0) {
      return 'failed';
    }
    if (this.errorCount === 0) {
      return 'success';
    }
    return 'partial';
  }

  get confirmApplyMessage(): string {
    const total = this.validationRows.length;
    const valid = this.validCount;

    if (this.confirmApplyStatus === 'success') {
      return `Success. ${valid} of ${total} rows will be applied.`;
    }
    if (this.confirmApplyStatus === 'partial') {
      return `Partial Success. ${valid} of ${total} rows will be applied. Rows with errors are skipped, not blocking valid rows.`;
    }
    return 'Failed. Recheck the uploaded import file to fix errors before proceeding.';
  }

  onDownloadTemplate(): void {
    if (this.downloadingTemplate) {
      return;
    }
    this.downloadingTemplate = true;
    this.spinner.show();

    this.push(
      this.shiftsService.downloadShiftAssignmentImportSample().subscribe(
        (res: any) => {
          this.downloadingTemplate = false;
          this.spinner.hide();

          const blob = res?.body instanceof Blob ? res.body : res;
          const fileName = this.getFileNameFromResponse(res) || 'shift-assignment-import-sample.csv';
          FileSaver.saveAs(blob, fileName);
        },
        (err: any) => {
          this.downloadingTemplate = false;
          this.spinner.hide();
          this.errorUtil.setErrorMessage(
            err.status,
            err.error ? err.error.title : null,
            err.error ? err.error.title : null,
            'error',
            2000
          );
        }
      )
    );
  }

  private getFileNameFromResponse(res: any): string | null {
    const contentDisposition =
      res?.headers?.get?.('content-disposition') ||
      res?.headers?.get?.('Content-Disposition');
    if (!contentDisposition) {
      return null;
    }

    const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
    if (!match?.[1]) {
      return null;
    }
    return match[1].replace(/['"]/g, '').trim();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    if (!this.isCsvFile(file)) {
      this.errorUtil.setErrorMessage(
        400,
        null,
        'Only CSV files are allowed',
        'error',
        3000
      );
      this.resetFileInput();
      return;
    }

    this.selectedFile = file;
    this.uploadedFileName = file.name;
    this.validationRows = [];
    this.csvSourceRows = [];

    // Parse CSV locally so preview can show short code / dates even if API omits them
    const reader = new FileReader();
    reader.onload = () => {
      this.csvSourceRows = this.parseCsvSourceRows(String(reader.result || ''));
      this.validateUploadedFile(file);
    };
    reader.onerror = () => {
      this.csvSourceRows = [];
      this.validateUploadedFile(file);
    };
    reader.readAsText(file);
  }

  private isCsvFile(file: File): boolean {
    const name = (file.name || '').toLowerCase();
    const type = (file.type || '').toLowerCase();
    return (
      name.endsWith('.csv') ||
      type === 'text/csv' ||
      type === 'application/csv' ||
      type === 'application/vnd.ms-excel'
    );
  }

  private validateUploadedFile(file: File): void {
    if (this.validating) {
      return;
    }
    this.validating = true;
    this.spinner.show();

    this.push(
      this.shiftsService.validateShiftAssignmentImport(file).subscribe(
        (res: any) => {
          this.validating = false;
          this.spinner.hide();

          if (res.status === 200 || res.status === 201) {
            this.validationRows = this.mapValidationRows(res.body ?? res);
          } else {
            this.validationRows = [];
            this.errorUtil.setErrorMessage(res.status, null);
          }
        },
        (err: any) => {
          this.validating = false;
          this.spinner.hide();
          this.validationRows = [];
          this.errorUtil.setErrorMessage(
            err.status,
            err.error ? err.error.title : null,
            err.error ? err.error.title || err.error.message : null,
            'error',
            3000
          );
        }
      )
    );
  }

  private mapValidationRows(body: any): ValidationRow[] {
    const list = Array.isArray(body)
      ? body
      : (body?.rows ?? body?.content ?? body?.data ?? body?.items ?? body?.preview ?? []);

    if (!Array.isArray(list)) {
      return [];
    }

    return list.map((item: any, index: number) => {
      const isValid = this.resolveIsValid(item);
      const errorReason = this.resolveErrorReason(item, isValid);
      const rowNumber = Number(item.rowNumber ?? item.row ?? item.rowIndex ?? index + 2);
      const csvRow =
        this.csvSourceRows.find((r) => r.rowNumber === rowNumber) ||
        this.csvSourceRows[index] ||
        null;

      const start =
        this.pickFirst(item, [
          'start',
          'effectiveStartDate',
          'startDate',
          'effStart',
          'effective_start_date',
          'start_date',
        ]) ||
        csvRow?.start ||
        '';
      const end =
        this.pickFirst(item, [
          'end',
          'effectiveEndDate',
          'endDate',
          'effEnd',
          'effective_end_date',
          'end_date',
        ]) ||
        csvRow?.end ||
        '';
      const shiftCode =
        this.pickFirst(item, [
          'shiftCode',
          'shiftTypeCode',
          'shortCode',
          'short_code',
          'code',
          'shift_type_code',
          'shiftType',
        ]) ||
        item?.shiftType?.code ||
        item?.shiftType?.shortCode ||
        csvRow?.shiftCode ||
        this.extractShiftCodeFromError(errorReason) ||
        '—';
      const identifier =
        this.pickFirst(item, [
          'identifier',
          'userOrGroup',
          'assigneeName',
          'email',
          'userEmail',
          'groupName',
          'login',
          'userName',
        ]) ||
        csvRow?.identifier ||
        '—';

      return {
        rowNumber,
        identifier,
        shiftCode,
        start: this.formatDate(start) || '—',
        end: this.formatDate(end) || '—',
        isValid,
        errorReason,
      };
    });
  }

  private pickFirst(item: any, keys: string[]): string {
    if (!item) {
      return '';
    }
    for (const key of keys) {
      const value = item[key];
      if (value != null && String(value).trim() !== '') {
        return String(value).trim();
      }
    }
    // Also check nested original/source/raw payloads from validators
    const nested = item.original || item.source || item.raw || item.csvRow || item.input;
    if (nested && nested !== item) {
      return this.pickFirst(nested, keys);
    }
    return '';
  }

  private extractShiftCodeFromError(errorReason?: string): string {
    if (!errorReason) {
      return '';
    }
    // e.g. "Shift code not found : NIGHT"
    const match = /shift\s*code[^:]*:\s*([A-Za-z0-9_-]+)/i.exec(errorReason);
    return match?.[1] || '';
  }

  /** Parse uploaded CSV so preview columns can be filled from source data */
  private parseCsvSourceRows(text: string): CsvSourceRow[] {
    const lines = text
      .replace(/^\uFEFF/, '')
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    if (lines.length < 2) {
      return [];
    }

    const headers = this.splitCsvLine(lines[0]).map((h) =>
      h.trim().toLowerCase().replace(/[\s_]+/g, '')
    );

    const col = (aliases: string[]) => {
      for (const alias of aliases) {
        const idx = headers.indexOf(alias);
        if (idx >= 0) {
          return idx;
        }
      }
      return -1;
    };

    const assigneeTypeIdx = col(['assigneetype', 'assignee', 'type']);
    const identifierIdx = col(['identifier', 'userorgroupidentifier', 'email', 'login', 'groupname', 'user']);
    const shiftCodeIdx = col(['shifttypecode', 'shiftcode', 'shortcode', 'code', 'shifttype']);
    const startIdx = col(['effectivestartdate', 'startdate', 'start', 'effstart']);
    const endIdx = col(['effectiveenddate', 'enddate', 'end', 'effend']);

    const rows: CsvSourceRow[] = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = this.splitCsvLine(lines[i]);
      const get = (idx: number) => (idx >= 0 && idx < cols.length ? cols[idx].trim() : '');
      rows.push({
        rowNumber: i + 1, // CSV row number including header
        assigneeType: get(assigneeTypeIdx),
        identifier: get(identifierIdx),
        shiftCode: get(shiftCodeIdx),
        start: get(startIdx),
        end: get(endIdx),
      });
    }
    return rows;
  }

  private splitCsvLine(line: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
        continue;
      }
      if (ch === ',' && !inQuotes) {
        result.push(current);
        current = '';
        continue;
      }
      current += ch;
    }
    result.push(current);
    return result;
  }

  private resolveIsValid(item: any): boolean {
    if (typeof item.isValid === 'boolean') {
      return item.isValid;
    }
    if (typeof item.valid === 'boolean') {
      return item.valid;
    }
    if (typeof item.success === 'boolean') {
      return item.success;
    }

    const result = String(item.result || item.status || '').toUpperCase();
    if (result === 'VALID' || result === 'SUCCESS' || result === 'OK') {
      return true;
    }
    if (result === 'ERROR' || result === 'INVALID' || result === 'FAILED') {
      return false;
    }

    const error =
      item.errorReason || item.error || item.message || item.reason || item.validationMessage;
    return !error;
  }

  private resolveErrorReason(item: any, isValid: boolean): string | undefined {
    if (isValid) {
      return undefined;
    }
    return (
      item.errorReason ||
      item.error ||
      item.message ||
      item.reason ||
      item.validationMessage ||
      (item.result && String(item.result).toLowerCase() !== 'error' ? item.result : null) ||
      'Error'
    );
  }

  private formatDate(value: any): string {
    if (!value) {
      return '';
    }
    const raw = String(value).trim();
    if (!raw || raw === '—' || raw.toLowerCase() === 'null') {
      return '';
    }
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
      return raw.substring(0, 10);
    }
    return raw;
  }

  private resetFileInput(): void {
    this.selectedFile = null;
    this.uploadedFileName = null;
    this.validationRows = [];
    this.csvSourceRows = [];
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  onConfirmApply(): void {
    if (!this.selectedFile || this.validCount === 0 || this.applying) {
      return;
    }

    this.applying = true;
    this.spinner.show();

    this.push(
      this.shiftsService.applyShiftAssignmentImport(this.selectedFile).subscribe(
        (res: any) => {
          this.applying = false;
          this.spinner.hide();

          if (res.status === 200 || res.status === 201) {
            this.errorUtil.setErrorMessage(
              200,
              this.translate?.general?.messages?.savedSuccessfully ||
                'Assignments imported successfully',
              null,
              'success',
              3000
            );
            this.resetFileInput();
            this.modalRef.hide();
          } else {
            this.errorUtil.setErrorMessage(res.status, null);
          }
        },
        (err: any) => {
          this.applying = false;
          this.spinner.hide();
          this.errorUtil.setErrorMessage(
            err.status,
            err.error ? err.error.title : null,
            err.error ? err.error.title || err.error.message : null,
            'error',
            3000
          );
        }
      )
    );
  }

  onDownloadErrorReport(): void {
    const errorRows = this.validationRows.filter((r) => !r.isValid);
    if (errorRows.length === 0) {
      return;
    }

    const headers = ['Row', 'Identifier', 'Shift Code', 'Start', 'End', 'Error'];
    const lines = [
      headers.join(','),
      ...errorRows.map((row) =>
        [
          row.rowNumber,
          this.escapeCsv(row.identifier),
          this.escapeCsv(row.shiftCode),
          this.escapeCsv(row.start || ''),
          this.escapeCsv(row.end || ''),
          this.escapeCsv(row.errorReason || 'Error'),
        ].join(',')
      ),
    ];

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const baseName = (this.uploadedFileName || 'shift-assignment-import').replace(/\.csv$/i, '');
    FileSaver.saveAs(blob, `${baseName}-errors.csv`);
  }

  private escapeCsv(value: string | number): string {
    const str = String(value ?? '');
    if (/[",\n\r]/.test(str)) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  }

  onCancelImport(): void {
    this.resetFileInput();
    this.modalRef.hide();
  }

  push(obs: any) {
    super.push(obs);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }
}
